import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, messageForError } from '../../api/problem';
import { Icon, type IconName } from '../Icon';

/**
 * The four states every data-bearing screen has to handle: loading, empty, error and offline.
 *
 * Defined once, at Milestone 0, before there is any data to load. Screens built later inherit a
 * consistent, translated, accessible treatment instead of each inventing a spinner and an
 * apologetic sentence — and the offline case, which matters on a clinic's connection (NFR-010),
 * is impossible to forget when it is already in the toolbox.
 *
 * Every state now carries a mark, because an empty screen with nothing at all on it reads as a
 * screen that failed rather than as one with nothing in it yet — that ambiguity is the whole
 * reason an empty state is a designed thing and not just a sentence.
 */

/**
 * Loading.
 *
 * Two shapes, and which one a screen gets is not a style choice. Where the shape of what is
 * arriving is known — a table, a list of rows — a skeleton says "a table is coming" and holds the
 * layout still, so nothing jumps when the data lands. Where it is not, a spinner is honest.
 */
export function LoadingState({ label, rows }: { readonly label?: string; readonly rows?: number }) {
  const { t } = useTranslation();

  if (rows && rows > 0) {
    return (
      <div className="skeleton-stack" role="status" aria-live="polite" aria-busy="true">
        <span className="visually-hidden">{label ?? t('states.loading.title')}</span>
        {Array.from({ length: rows }, (_, index) => (
          <span
            key={index}
            className="skeleton skeleton--row"
            aria-hidden="true"
            // A row of identical bars reads as a pattern rather than as content arriving. The
            // widths step down the way a real table's do, and the fade removes the hard edge at
            // the bottom of the stack.
            style={{ inlineSize: `${100 - index * 4}%`, opacity: 1 - index * 0.12 }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p className="state__title">{label ?? t('states.loading.title')}</p>
      <p className="state__description">{t('states.loading.description')}</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon = 'search',
}: {
  readonly title?: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly icon?: IconName;
}) {
  const { t } = useTranslation();
  return (
    <div className="state">
      <span className="state__mark" aria-hidden="true">
        <Icon name={icon} size={22} />
      </span>
      <p className="state__title">{title ?? t('states.empty.title')}</p>
      <p className="state__description">{description ?? t('states.empty.description')}</p>
      {action ? <div className="state__actions">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { readonly error: unknown; readonly onRetry?: () => void }) {
  const { t } = useTranslation();
  const requestId = error instanceof ApiError ? error.requestId : undefined;

  return (
    <div className="state state--error" role="alert">
      <span className="state__mark" aria-hidden="true">
        <Icon name="alert" size={22} />
      </span>
      <p className="state__title">{t('states.error.title')}</p>
      <p className="state__description">
        {messageForError(error, t as (key: string, options?: { defaultValue?: string }) => string)}
      </p>
      {/* The request id is what turns "it broke" into something support can trace, so it is
          offered rather than buried — but quietly, because it is for the second conversation and
          not the first. */}
      {requestId ? (
        <p className="muted">
          <span>{t('states.error.requestId')}: </span>
          <code>{requestId}</code>
        </p>
      ) : null}
      {onRetry ? (
        <div className="state__actions">
          <button type="button" className="button" onClick={onRetry}>
            <Icon name="refunds" size={18} />
            {t('common.retry')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function OfflineState({ onRetry }: { readonly onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="state state--offline" role="status">
      <span className="state__mark" aria-hidden="true">
        <Icon name="live" size={22} />
      </span>
      <p className="state__title">{t('states.offline.title')}</p>
      <p className="state__description">{t('states.offline.description')}</p>
      {onRetry ? (
        <div className="state__actions">
          <button type="button" className="button" onClick={onRetry}>
            <Icon name="refunds" size={18} />
            {t('common.retry')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function ForbiddenState() {
  const { t } = useTranslation();
  return (
    <div className="state" role="alert">
      <span className="state__mark" aria-hidden="true">
        <Icon name="policies" size={22} />
      </span>
      <p className="state__title">{t('states.forbidden.title')}</p>
      <p className="state__description">{t('states.forbidden.description')}</p>
    </div>
  );
}

export function NotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="state">
      <span className="state__mark" aria-hidden="true">
        <Icon name="search" size={22} />
      </span>
      <p className="state__title">{t('states.notFound.title')}</p>
      <p className="state__description">{t('states.notFound.description')}</p>
    </div>
  );
}
